import { Foundation } from 'src/.generated/Foundation.EnterpriseWeb.model';
import { Feature } from 'src/.generated/Feature.EnterpriseWeb.model';
import { useTheme } from 'lib/context/ThemeContext';
import Headline from 'src/helpers/Headline/Headline';
import Component from 'src/helpers/Component/Component';
import BodyCopy from 'src/helpers/BodyCopy/BodyCopy';
import OrangeTriangle from 'src/helpers/SvgIcon/icons/icon--orange-triangle';
import ButtonGroup from 'src/helpers/ButtonGroup/ButtonGroup';
import Button from 'src/helpers/Button/Button';
import { cta1ToButtonProps, cta2ToButtonProps } from 'src/helpers/Button/Utils';
import { ListHighlightAndCtaTheme } from './ListHighlightAndCta.theme';

import { GetStaticComponentProps, withDatasourceCheck } from '@sitecore-jss/sitecore-jss-nextjs';
import { mapItemFieldResultsToObject } from 'lib/graphql/mappers/map-item-field-results-to-object';
import { mapSearchResults } from 'lib/graphql/mappers/map-search-results';
import { IntegratedGraphQlResult } from 'lib/graphql/types/integrated-graphql-result';
import { ItemFieldResult } from 'lib/graphql/types/item-field-result';
import { ItemSearchResults } from 'lib/graphql/types/item-search-results';
import { useMemo } from 'react';

export type ListHighlightAndCtaProps =
  Feature.EnterpriseWeb.Enterprise.Components.Listing.ListHighlightAndCta.ListHighlightAndCta;
export type ListHighlightAndCtaItemProps =
  Feature.EnterpriseWeb.Enterprise.Components.Listing.ListHighlightAndCta.ListHighlightAndCtaItem;

const ListHighlightAndCta = (props: ListHighlightAndCtaProps): JSX.Element => {
  const { fields } = props;
  const { themeData, themeName } = useTheme(ListHighlightAndCtaTheme());
  const compFields: any = useMemo(() => {
    if (fields?.data) {
      const mappedFields = fields.data.item.fields.map((field: any) => {
        return { [field.name]: field.jsonValue };
      });
      const result = Object.assign({}, ...mappedFields);
      const mappedChildrenFields = fields.data.item.children?.results?.map((field: any) => {
        return {
          fields: Object.assign(
            {},
            ...field?.fields?.map((innerField: any) => {
              return { [innerField.name]: innerField.jsonValue };
            })
          ),
        };
      });

      result.children = mappedChildrenFields;

      return result;
    }

    return fields;
  }, [fields?.data]);

  const sectionHeadline: Foundation.EnterpriseWeb.Enterprise.FieldSets.Headline = {
    fields: {
      headlineText: compFields?.sectionHeadline,
      headlineLevel: compFields?.sectionHeadlineLevel,
    },
  };
  const listItemStyle = compFields?.listItemStyle && compFields?.listItemStyle?.fields?.Value.value;

  return (
    <Component variant="lg" dataComponent="listing/listhighlightandcta" {...props}>
      <div className={themeData.classes.headlineBorder}>
        <div className="mt-4 flex md:mt-2">
          {themeName === 'aw' && (
            <div className="m-1 mt-[1px] flex">
              <OrangeTriangle></OrangeTriangle>
            </div>
          )}
          <Headline classes={themeData.classes.headlineClass} fields={sectionHeadline.fields} />
        </div>
      </div>

      {/* loop through children */}
      <div className="col-span-12 md:col-span-8">
        {compFields?.children?.map((_item: ListHighlightAndCtaProps, i: number) => {
          if (_item.fields && listItemStyle === 'row-list') {
            _item.fields.cta1Link.value.text =
              _item.fields.headlineText.value !== ''
                ? _item.fields.headlineText.value
                : _item.fields.cta1Link.value.text;
          }
          const variantLinkRightIcon = {
            name: 'Link Right Icon',
            fields: {
              Value: {
                value: 'link-right-icon',
              },
            },
            id: '',
            url: '',
          };

          return (
            <div key={i} className={themeData.classes.listItemContainer}>
              <div className={themeData.classes.listItemHeadlineBorder}>
                <div>
                  {listItemStyle !== 'row-list' && (
                    <>
                      <Headline
                        classes={themeData.classes.contentClasses.listItemHeadlineClass}
                        fields={_item.fields}
                      />
                      <BodyCopy
                        fields={_item.fields}
                        classes={themeData.classes.contentClasses.body}
                      />
                    </>
                  )}

                  {listItemStyle === 'row-list' ? (
                    <Button
                      field={_item.fields?.cta1Link}
                      variant={variantLinkRightIcon}
                      icon={_item.fields?.cta1Icon}
                      classes={(themeData.classes.buttonGroupClassRightIcon, 'font-sans text-s')}
                    ></Button>
                  ) : (
                    <ButtonGroup
                      cta1={cta1ToButtonProps(
                        _item,
                        themeData.classes.buttonGroupClass.cta1Classes
                      )}
                      cta2={cta2ToButtonProps(
                        _item,
                        themeData.classes.buttonGroupClass.cta2Classes
                      )}
                      wrapperClasses={themeData.classes.buttonGroupClass.wrapper}
                    />
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </Component>
  );
};
type IntegratedGraphQl = IntegratedGraphQlResult<{
  item: {
    fields: ItemFieldResult[];
    children: ItemSearchResults<{
      template: { name: string };
      fields: ItemFieldResult[];
    }>;
  };
}>;

export const getStaticProps: GetStaticComponentProps = async (rendering) => {
  if (rendering.fields === undefined || !('data' in rendering.fields)) {
    return {};
  }

  const fields = rendering.fields as unknown as IntegratedGraphQl;

  const result = {
    fields: {
      ...mapItemFieldResultsToObject(fields.data.item.fields),
      children: mapSearchResults(fields.data.item.children, (child) => ({
        fields: {
          ...mapItemFieldResultsToObject(child.fields),
        },
      })),
    },
  };
  return result;
};

export default withDatasourceCheck()(ListHighlightAndCta);
